import { createBTOSCore } from "./btos-core.js";

export const RESERVATION_JOURNEY_SELECTOR = "[data-reservation-journey]";
export const RESERVATION_JOURNEY_VERSION = "CUSTOMER-JOURNEY-01-v1";
export const RESERVATION_EVENTS = Object.freeze([
  "availability_opened",
  "calendar_opened",
  "reservation_started",
  "reservation_confirmed",
  "waitlist_joined",
  "region_unavailable",
  "clubinho_selected",
  "rental_selected",
  "identity_checked",
  "reservation_rehydrated",
  "reservation_cancelled",
]);

export const DEFAULT_RESERVATION_CONFIG = Object.freeze({
  mode: "disabled",
  commercialStatus: "commercial_disabled",
  launchAuthorized: false,
  runtime: {
    endpoint: "/a/brinquetoy/commerce/reservations",
    identityEndpoint: "/a/brinquetoy/commerce/clubinho/membership",
    dashboardUrl: "/a/brinquetoy/commerce/clubinho",
    loginUrl: "/customer_authentication/login",
  },
  item: {
    handle: "produto-demo",
    title: "Produto BrinqueToy",
    creditoysCost: 0,
    priceLabel: "Valor nao configurado",
  },
  availability: {
    status: "commercial_unavailable",
    availableFrom: null,
    waitlistEnabled: false,
  },
  calendar: {
    startDate: "2026-08-01",
    days: 14,
    durationDays: 7,
    unavailableDates: [],
    blockedDates: [],
  },
  clubinho: {
    enabled: false,
    availableCapacity: 0,
  },
  rental: {
    enabled: false,
    durations: ["daily", "weekly", "biweekly", "monthly"],
    labels: {
      daily: "Diario",
      weekly: "Semanal",
      biweekly: "Quinzenal",
      monthly: "Mensal",
    },
  },
  regions: [
    { id: "not_configured", name: "Em validacao interna", status: "planned", deliveryEstimate: "Nao configurado" },
    { id: "baixada-fluminense", name: "Baixada Fluminense", status: "planned", deliveryEstimate: "Em breve" },
    { id: "grande-rio", name: "Grande Rio", status: "planned", deliveryEstimate: "Em breve" },
  ],
  copy: {
    availableNow: "Disponibilidade comercial em preparacao.",
    availableFrom: "Experiencia em validacao interna.",
    waitlist: "Reserva online ainda nao disponivel.",
    regionUnavailable: "Disponibilidade comercial em preparacao.",
    notifyRegion: "Experiencia em validacao interna.",
    waitlistJoined: "Nenhuma solicitacao foi registrada.",
    confirm: "Simulacao interna",
    confirmed: "Simulacao interna concluida. Nenhuma reserva foi criada.",
  },
  projections: {
    lifecycle: {
      statusLabel: "Item aprovado para experiencia BrinqueToy",
      helper: "Curadoria, disponibilidade e comunicacao alinhadas ao catalogo oficial.",
    },
    search: {
      contextLabel: "Selecionado para familias que buscam desenvolvimento com leveza.",
      rankingLabel: "Recomendacao alinhada ao perfil do item.",
    },
    recommendations: {
      related: [],
      next: [],
      complementary: [],
      alternatives: [],
    },
  },
});

function clone(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

function asDate(value) {
  const date = value instanceof Date ? value : new Date(`${String(value).slice(0, 10)}T12:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? new Date("2026-08-01T12:00:00.000Z") : date;
}

function addDays(value, days) {
  const date = new Date(asDate(value).getTime());
  date.setUTCDate(date.getUTCDate() + days);
  return date;
}

function addMonths(value, months) {
  const date = new Date(asDate(value).getTime());
  date.setUTCMonth(date.getUTCMonth() + months);
  return date;
}

function isoDate(value) {
  return asDate(value).toISOString().slice(0, 10);
}

function formatDatePtBr(value) {
  const [year, month, day] = isoDate(value).split("-");
  return `${day}/${month}/${year}`;
}

function daysInMonth(value) {
  const date = asDate(value);
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
}

function unique(values = []) {
  return [...new Set(values.filter(Boolean))];
}

export function mergeReservationConfig(input = {}) {
  const config = clone(DEFAULT_RESERVATION_CONFIG);
  const next = clone(input);
  return {
    ...config,
    ...next,
    mode: ["preview", "pilot"].includes(next.mode) ? next.mode : "disabled",
    commercialStatus: "commercial_disabled",
    launchAuthorized: false,
    item: { ...config.item, ...(next.item || {}) },
    availability: { ...config.availability, ...(next.availability || {}) },
    calendar: { ...config.calendar, ...(next.calendar || {}) },
    clubinho: { ...config.clubinho, ...(next.clubinho || {}) },
    rental: {
      ...config.rental,
      ...(next.rental || {}),
      labels: { ...config.rental.labels, ...(next.rental?.labels || {}) },
      durations: unique(next.rental?.durations || config.rental.durations),
    },
    regions: Array.isArray(next.regions) && next.regions.length > 0 ? next.regions : config.regions,
    copy: { ...config.copy, ...(next.copy || {}) },
    runtime: { ...config.runtime, ...(next.runtime || {}) },
    projections: {
      ...config.projections,
      ...(next.projections || {}),
      lifecycle: { ...config.projections.lifecycle, ...(next.projections?.lifecycle || {}) },
      search: { ...config.projections.search, ...(next.projections?.search || {}) },
      recommendations: {
        ...config.projections.recommendations,
        ...(next.projections?.recommendations || {}),
      },
    },
  };
}

export function createSafeAvailabilityProjection(availability = {}, copy = DEFAULT_RESERVATION_CONFIG.copy) {
  const status = availability.status || "commercial_unavailable";
  if (status === "available") {
    return {
      status,
      label: copy.availableNow,
      cta: copy.confirm,
      waitlist: false,
      public: true,
    };
  }
  if (status === "available_from") {
    return {
      status,
      label: `${copy.availableFrom} ${formatDatePtBr(availability.availableFrom || "2026-08-08")}`,
      cta: copy.confirm,
      waitlist: false,
      public: true,
    };
  }
  return {
    status: "waitlist_only",
    label: copy.waitlist,
    cta: copy.waitlist,
    waitlist: true,
    public: true,
  };
}

export function buildCalendarIntervals(calendar = DEFAULT_RESERVATION_CONFIG.calendar) {
  const unavailable = new Set(calendar.unavailableDates || []);
  const blocked = new Set(calendar.blockedDates || []);
  const monthStart = asDate(calendar.startDate);
  monthStart.setUTCDate(1);
  return Array.from({ length: daysInMonth(monthStart) }, (_, index) => {
    const date = isoDate(addDays(monthStart, index));
    const status = blocked.has(date) || unavailable.has(date) ? "unavailable" : "available";
    return {
      date,
      label: formatDatePtBr(date),
      status,
      available: status === "available",
      period: {
        start: date,
        end: isoDate(addDays(date, Number(calendar.durationDays || 7))),
      },
    };
  });
}

export function createReservationJourneyState(configInput = {}) {
  const config = mergeReservationConfig(configInput);
  const calendar = buildCalendarIntervals(config.calendar);
  const selectedRegion = config.regions.find((region) => ["active", "pilot"].includes(region.status)) || config.regions[0] || null;
  const selectedDate = calendar.find((day) => day.available) || calendar[0] || null;
  return {
    version: RESERVATION_JOURNEY_VERSION,
    config,
    availability: createSafeAvailabilityProjection(config.availability, config.copy),
    calendar,
    modality: config.clubinho.enabled ? "clubinho" : "rental",
    rentalDuration: config.rental.durations[0] || "weekly",
    selectedRegion,
    selectedDate,
    calendarMonthOffset: 0,
    postalCodeValid: true,
    waitlistJoined: false,
    confirmed: false,
    rehydrating: config.mode === "pilot",
    cancelling: false,
    identity: {
      status: config.mode === "pilot" ? "checking" : "not_required",
      authenticated: false,
      authorized: config.mode !== "pilot",
    },
    events: [],
  };
}

export function navigateCalendarMonth(state, direction) {
  const next = clone(state);
  next.calendarMonthOffset += direction < 0 ? -1 : 1;
  const startDate = isoDate(addMonths(next.config.calendar.startDate, next.calendarMonthOffset));
  next.calendar = buildCalendarIntervals({ ...next.config.calendar, startDate });
  next.selectedDate = next.calendar.find((day) => day.available) || next.calendar[0] || null;
  next.events.push(sanitizeJourneyEvent("calendar_opened", { direction: direction < 0 ? "previous" : "next" }));
  return next;
}

export function validatePostalCode(value) {
  const compact = String(value || "").replace(/\D/g, "");
  return compact.length === 0 || compact.length === 8;
}

export function selectModality(state, modality) {
  const next = clone(state);
  next.modality = modality === "rental" ? "rental" : "clubinho";
  next.events.push(sanitizeJourneyEvent(next.modality === "rental" ? "rental_selected" : "clubinho_selected", {
    modality: next.modality,
  }));
  return next;
}

export function selectRentalDuration(state, duration) {
  const next = clone(state);
  if (next.config.rental.durations.includes(duration)) {
    next.rentalDuration = duration;
  }
  return next;
}

export function selectCalendarDate(state, date) {
  const next = clone(state);
  const candidate = next.calendar.find((day) => day.date === date);
  if (candidate && candidate.available) {
    next.selectedDate = candidate;
  }
  next.events.push(sanitizeJourneyEvent("calendar_opened", { selected: Boolean(candidate?.available) }));
  return next;
}

export function selectRegion(state, regionId) {
  const next = clone(state);
  const region = next.config.regions.find((candidate) => candidate.id === regionId) || null;
  next.selectedRegion = region || next.selectedRegion;
  if (region && !["active", "pilot"].includes(region.status)) {
    next.events.push(sanitizeJourneyEvent("region_unavailable", { region_status: region.status }));
  }
  return next;
}

export function joinWaitlist(state) {
  const next = clone(state);
  next.waitlistJoined = true;
  next.events.push(sanitizeJourneyEvent("waitlist_joined", { item_handle: next.config.item.handle }));
  return next;
}

export function createReservationSummary(state) {
  const region = state.selectedRegion || {};
  const date = state.selectedDate || {};
  const isPreview = state.config.mode === "preview";
  const creditoysAfter = Math.max(
    0,
    Number(state.config.clubinho.availableCapacity || 0) - Number(state.config.item.creditoysCost || 0)
  );
  const regionServed = ["active", "pilot"].includes(region.status);
  return {
    item: state.config.item.title,
    period: date.period ? `${formatDatePtBr(date.period.start)} a ${formatDatePtBr(date.period.end)}` : "Escolha um periodo",
    modality: state.modality === "clubinho" ? "Clubinho" : "Aluguel avulso",
    region: region.name || "Regiao nao selecionada",
    delivery: regionServed ? region.deliveryEstimate || "Entrega programada" : state.config.copy.regionUnavailable,
    commitment: isPreview
      ? "Condicoes em preparacao"
      : state.modality === "clubinho"
      ? `${state.config.item.creditoysCost} Creditoys agora, ${creditoysAfter} depois da reserva`
      : state.config.item.priceLabel,
    canConfirm: Boolean(!isPreview && regionServed && date.available && !state.availability.waitlist && state.postalCodeValid),
  };
}

export function createPdpProjectionViewModel(state) {
  const projections = state.config.projections || DEFAULT_RESERVATION_CONFIG.projections;
  const normalizeList = (items = []) => items
    .filter((item) => item?.title || item?.handle)
    .slice(0, 4)
    .map((item) => ({
      title: item.title || item.handle,
      handle: item.handle || "",
      label: item.label || "Ver item",
    }));
  return {
    lifecycle: clone(projections.lifecycle),
    search: clone(projections.search),
    recommendations: {
      related: normalizeList(projections.recommendations?.related),
      next: normalizeList(projections.recommendations?.next),
      complementary: normalizeList(projections.recommendations?.complementary),
      alternatives: normalizeList(projections.recommendations?.alternatives),
    },
  };
}

export function evaluateThreeDecisionJourney(state) {
  const decisions = [
    { id: "period", label: "Quando reservar", satisfied: Boolean(state.selectedDate?.available) },
    { id: "modality", label: "Como usar", satisfied: ["clubinho", "rental"].includes(state.modality) },
    { id: "confirmation", label: "Confirmar", satisfied: Boolean(state.selectedRegion?.id && state.postalCodeValid) },
  ];
  return {
    maxCognitiveDecisions: 3,
    decisions,
    pass: decisions.length <= 3 && decisions.every((decision) => decision.satisfied),
  };
}

export function confirmReservation(state) {
  const next = clone(state);
  const summary = createReservationSummary(next);
  if (!summary.canConfirm) {
    return next.availability.waitlist ? joinWaitlist(next) : next;
  }
  if (next.config.mode === "pilot") {
    next.persistenceUnavailable = true;
    next.events.push(sanitizeJourneyEvent("reservation_started", { modality: next.modality, persistence: "pending" }));
    return next;
  }
  if (next.config.mode !== "preview") return next;
  next.confirmed = true;
  next.events.push(sanitizeJourneyEvent("reservation_started", { modality: next.modality }));
  next.events.push(sanitizeJourneyEvent("reservation_confirmed", { modality: next.modality, simulation: true }));
  return next;
}

export function createPilotReservationIntent(state) {
  const summary = createReservationSummary(state);
  if (!summary.canConfirm || state.config.mode !== "pilot") throw new Error("pilot_intent_not_ready");
  const start = asDate(state.selectedDate.date);
  return {
    itemReference: state.config.item.handle,
    modality: state.modality,
    durationType: state.modality === "rental" ? state.rentalDuration : undefined,
    period: {
      start: start.toISOString(),
      end: addDays(start, state.config.calendar.durationDays).toISOString(),
    },
    region: state.selectedRegion.id,
    templateMode: "pilot",
  };
}

export async function persistPilotReservation(state, {
  fetchImpl = fetch,
  endpoint = state.config.runtime?.endpoint || "/a/brinquetoy/commerce/reservations",
  idempotencyKey = globalThis.crypto?.randomUUID?.() || `pilot-${Date.now()}`,
} = {}) {
  const response = await fetchImpl(endpoint, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(createPilotReservationIntent(state)),
    credentials: "same-origin",
  });
  const responseBody = await response.text();
  let payload;
  try {
    payload = JSON.parse(responseBody);
  } catch {
    throw new Error("pilot_response_invalid");
  }
  if (!response.ok || !payload.success) {
    throw new Error(payload.errors?.[0]?.code || "pilot_persistence_failed");
  }
  if (!payload.data?.reservationReference) {
    throw new Error("pilot_response_incomplete");
  }
  return {
    ...clone(state),
    confirmed: true,
    persistenceUnavailable: false,
    pilotReference: payload.data.reservationReference,
    pilotStatus: payload.data.status,
    events: [
      ...state.events,
      sanitizeJourneyEvent("reservation_confirmed", {
        modality: state.modality,
        simulation: false,
        mode: "pilot",
      }),
    ],
  };
}

export async function readPilotReservations(state, {
  fetchImpl = fetch,
  endpoint = state.config.runtime?.endpoint || "/a/brinquetoy/commerce/reservations",
} = {}) {
  const url = new URL(endpoint, globalThis.location?.origin || "https://brinquetoy.com.br");
  url.searchParams.set("itemReference", state.config.item.handle);
  url.searchParams.set("status", "active");
  const response = await fetchImpl(url.pathname + url.search, {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "same-origin",
    cache: "no-store",
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.errors?.[0]?.code || "pilot_read_failed");
  return Array.isArray(payload.data?.reservations) ? payload.data.reservations : [];
}

export async function rehydratePilotReservation(state, options = {}) {
  const reservations = await readPilotReservations(state, options);
  const reservation = reservations[0];
  if (!reservation) return { ...clone(state), confirmed: false, rehydrating: false, pilotReference: null, pilotStatus: null };
  const next = clone(state);
  next.confirmed = true;
  next.rehydrating = false;
  next.persistenceUnavailable = false;
  next.pilotReference = reservation.reservationReference;
  next.pilotStatus = reservation.status;
  next.modality = reservation.modality;
  next.selectedRegion = next.config.regions.find((region) => region.id === reservation.region) || next.selectedRegion;
  const start = isoDate(reservation.period?.start);
  const end = isoDate(reservation.period?.end);
  next.selectedDate = { date: start, label: formatDatePtBr(start), status: "available", available: true, period: { start, end } };
  next.events.push(sanitizeJourneyEvent("reservation_rehydrated", { status: reservation.status }));
  return next;
}

export async function cancelPilotReservation(state, {
  fetchImpl = fetch,
  endpoint = state.config.runtime?.endpoint || "/a/brinquetoy/commerce/reservations",
  idempotencyKey = globalThis.crypto?.randomUUID?.() || `pilot-cancel-${Date.now()}`,
} = {}) {
  if (!state.pilotReference) throw new Error("reservation_not_found");
  const response = await fetchImpl(`${endpoint}/${encodeURIComponent(state.pilotReference)}/cancel`, {
    method: "POST",
    headers: { "content-type": "application/json", "idempotency-key": idempotencyKey },
    body: JSON.stringify({ templateMode: "pilot" }),
    credentials: "same-origin",
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) throw new Error(payload.errors?.[0]?.code || "pilot_cancel_failed");
  const next = clone(state);
  next.confirmed = false;
  next.cancelling = false;
  next.pilotStatus = payload.data?.status || "pilot_cancelled";
  next.pilotReference = null;
  next.events.push(sanitizeJourneyEvent("reservation_cancelled", { status: next.pilotStatus }));
  return next;
}

function safePilotError(code) {
  const normalized = String(code || "");
  const messages = {
    customer_login_required: "Entre na conta sintetica autorizada para continuar o piloto.",
    customer_not_allowlisted: "Esta conta nao esta autorizada para o piloto.",
    pilot_membership_required: "A associacao sintetica do piloto precisa ser preparada.",
    synthetic_capacity_insufficient: "A capacidade sintetica do piloto e insuficiente.",
    reservation_conflict: "O periodo selecionado ja esta reservado no piloto.",
    rate_limit_exceeded: "Aguarde um momento antes de repetir a operacao piloto.",
    pilot_response_invalid: "A resposta do piloto nao estava em formato JSON valido.",
    pilot_response_incomplete: "A resposta do piloto nao trouxe a referencia esperada.",
    internal_error: "O runtime piloto nao concluiu a resposta.",
    pilot_read_failed: "Nao foi possivel recuperar sua reserva. Atualize a pagina para tentar novamente.",
    pilot_cancel_failed: "Nao foi possivel cancelar agora. Sua reserva continua preservada.",
  };
  return {
    code: Object.hasOwn(messages, normalized) ? normalized : "pilot_persistence_failed",
    message: messages[normalized] || "A reserva piloto nao pode ser confirmada.",
  };
}

export async function probePilotIdentity({
  fetchImpl = fetch,
  endpoint = "/a/brinquetoy/commerce/clubinho/membership",
} = {}) {
  try {
    const response = await fetchImpl(endpoint, {
      method: "GET",
      headers: { accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
    });
    const payload = await response.json().catch(() => ({}));
    const code = String(payload.errors?.[0]?.code || "");
    if (response.ok && payload.success) {
      return { status: "authorized", authenticated: true, authorized: true };
    }
    if (code === "customer_login_required") {
      return { status: "login_required", authenticated: false, authorized: false };
    }
    if (code === "customer_not_allowlisted") {
      return { status: "not_allowlisted", authenticated: true, authorized: false };
    }
    return { status: "unavailable", authenticated: false, authorized: false, errorCode: code || "identity_probe_failed" };
  } catch {
    return { status: "unavailable", authenticated: false, authorized: false, errorCode: "identity_probe_failed" };
  }
}

export function sanitizeJourneyEvent(type, payload = {}) {
  const safeType = RESERVATION_EVENTS.includes(type) ? type : "reservation_started";
  const forbidden = /token|secret|gid:\/\/|email|phone|customerId|familyId|childId|fulfillment|demand_acquisition|supplier/iu;
  const safePayload = Object.entries(payload).reduce((acc, [key, value]) => {
    const serialized = String(value);
    if (forbidden.test(key) || forbidden.test(serialized)) return acc;
    acc[key] = value;
    return acc;
  }, {});
  return {
    type: safeType,
    payload: safePayload,
    occurredAt: new Date().toISOString(),
    sanitized: true,
  };
}

export function createReservationObservability(events = []) {
  return events.map((event) => sanitizeJourneyEvent(event.type, event.payload));
}

export function parseReservationConfigText(value) {
  const decoded = String(value || "")
    .replaceAll("&quot;", '"')
    .replaceAll("&#39;", "'")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&amp;", "&");
  return JSON.parse(decoded);
}

function readConfig(root) {
  const node = root.querySelector("[data-reservation-config]");
  if (!node?.textContent) return {};
  try {
    return parseReservationConfigText(node.textContent);
  } catch {
    return {};
  }
}

function emit(root, event) {
  root.dispatchEvent?.(new CustomEvent(`btos:${event.type}`, { detail: event, bubbles: true }));
}

function renderState(root, state) {
  const isPreview = state.config.mode === "preview";
  const status = root.querySelector("[data-reservation-status]");
  const calendar = root.querySelector("[data-reservation-calendar]");
  const summary = root.querySelector("[data-reservation-summary]");
  const regionNote = root.querySelector("[data-reservation-region-note]");
  const confirmation = root.querySelector("[data-reservation-confirmation]");
  const projection = root.querySelector("[data-reservation-projection]");
  const postalCode = root.querySelector("[data-reservation-postal-code]");
  const postalCodeNote = root.querySelector("[data-reservation-postal-code-note]");
  const identityStatus = root.querySelector("[data-reservation-identity-status]");
  const loginLink = root.querySelector("[data-reservation-login]");
  const confirmButton = root.querySelector("[data-reservation-action='confirm']");
  const waitlistButton = root.querySelector("[data-reservation-action='waitlist']");
  const cancelButton = root.querySelector("[data-reservation-action='cancel']");
  const dashboardLink = root.querySelector("[data-reservation-dashboard]");
  if (status) status.textContent = isPreview ? "Disponibilidade comercial em preparacao." : state.availability.label;
  if (calendar) {
    const monthDate = asDate(state.calendar[0]?.date || state.config.calendar.startDate);
    const leading = monthDate.getUTCDay();
    calendar.innerHTML = `
      <div class="reservation-journey__weekdays" aria-hidden="true">
        ${["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"].map((label) => `<span>${label}</span>`).join("")}
      </div>
      <div class="reservation-journey__month" style="--reservation-leading-days:${leading}">
      ${state.calendar.map((day, index) => `
      <button class="reservation-journey__day" type="button" data-date="${day.date}" data-state="${day.status}" aria-label="${day.label}" aria-pressed="${state.selectedDate?.date === day.date}" ${day.available ? "" : "disabled"}>
        <span>${index === 0 ? formatDatePtBr(day.date) : String(Number(day.date.slice(-2)))}</span>
      </button>
      `).join("")}
      </div>`;
  }
  if (postalCode) postalCode.setAttribute("aria-invalid", String(!state.postalCodeValid));
  if (postalCodeNote) {
    postalCodeNote.hidden = state.postalCodeValid;
    postalCodeNote.textContent = state.postalCodeValid ? "" : "Informe um CEP valido com 8 digitos.";
  }
  if (identityStatus) {
    const messages = {
      checking: "Validando a conta piloto.",
      authorized: "Conta sintetica autorizada para o piloto.",
      login_required: "Entre na conta sintetica autorizada para continuar.",
      not_allowlisted: "A conta atual nao esta autorizada para este piloto.",
      unavailable: "Nao foi possivel validar a conta piloto. Tente novamente.",
      not_required: "",
    };
    identityStatus.textContent = messages[state.identity?.status] || messages.unavailable;
    identityStatus.hidden = state.config.mode !== "pilot";
  }
  if (loginLink) {
    loginLink.href = state.config.runtime?.loginUrl || "/customer_authentication/login";
    loginLink.hidden = state.config.mode !== "pilot" || Boolean(state.identity?.authorized);
  }
  if (confirmButton) {
    confirmButton.disabled = isPreview || state.rehydrating || state.cancelling || state.confirmed || (state.config.mode === "pilot" && !state.identity?.authorized);
    confirmButton.textContent = isPreview ? "Reserva online ainda nao disponivel" : state.config.copy.confirm;
    confirmButton.hidden = state.confirmed;
  }
  if (waitlistButton) {
    waitlistButton.hidden = isPreview;
  }
  if (cancelButton) {
    cancelButton.hidden = !state.confirmed;
    cancelButton.disabled = state.cancelling;
    cancelButton.textContent = state.cancelling ? "Cancelando..." : "Cancelar reserva";
  }
  if (dashboardLink) {
    dashboardLink.href = state.config.runtime?.dashboardUrl || "/a/brinquetoy/commerce/clubinho";
    dashboardLink.hidden = state.config.mode !== "pilot" || !state.identity?.authorized;
  }
  const model = createReservationSummary(state);
  if (summary) {
    summary.innerHTML = `
      <li><strong>Item</strong><span>${escapeHtml(model.item)}</span></li>
      <li><strong>Periodo</strong><span>${escapeHtml(model.period)}</span></li>
      <li><strong>Modalidade</strong><span>${escapeHtml(model.modality)}</span></li>
      <li><strong>Regiao</strong><span>${escapeHtml(model.region)}</span></li>
      <li><strong>Entrega prevista</strong><span>${escapeHtml(model.delivery)}</span></li>
      <li><strong>${isPreview ? "Condicoes" : state.modality === "clubinho" ? "Creditoys" : "Valor"}</strong><span>${escapeHtml(model.commitment)}</span></li>
    `;
  }
  if (regionNote) {
    regionNote.textContent = model.canConfirm ? "" : state.config.copy.regionUnavailable;
    regionNote.hidden = model.canConfirm;
  }
  if (confirmation) {
    const pilotError = safePilotError(state.pilotError);
    confirmation.dataset.pilotErrorCode = state.pilotError ? pilotError.code : "";
    confirmation.textContent = state.confirmed
      ? state.config.mode === "pilot"
        ? `Reserva confirmada para esta experiencia. Referencia: ${state.pilotReference}.`
        : state.config.copy.confirmed
      : state.waitlistJoined
        ? state.config.copy.waitlistJoined
        : state.pilotError
          ? pilotError.message
        : "";
  }
  if (projection) {
    const model = createPdpProjectionViewModel(state);
    const renderList = (title, items) => items.length > 0
      ? `<section><h4>${escapeHtml(title)}</h4><ul>${items.map((item) => `<li><a href="/products/${escapeHtml(item.handle)}">${escapeHtml(item.title)}</a></li>`).join("")}</ul></section>`
      : "";
    projection.innerHTML = `
      <article>
        <h3>${escapeHtml(model.lifecycle.statusLabel)}</h3>
        <p>${escapeHtml(model.lifecycle.helper)}</p>
        <p>${escapeHtml(model.search.contextLabel)}</p>
        <p>${escapeHtml(model.search.rankingLabel)}</p>
      </article>
      ${renderList("Proximos passos", model.recommendations.next)}
      ${renderList("Combina com", model.recommendations.complementary)}
      ${renderList("Alternativas", model.recommendations.alternatives)}
    `;
  }
}

export class ReservationJourneyApp {
  constructor(root) {
    this.root = root;
    this.core = createBTOSCore({ namespace: "reservation-journey", logLevel: "silent" });
    this.state = createReservationJourneyState(readConfig(root));
    this.bind();
    this.track("availability_opened");
    this.render();
    void this.initializePilot();
  }

  bind() {
    this.root.addEventListener?.("click", async (event) => {
      const target = event.target?.closest?.("[data-reservation-action], [data-date]");
      if (!target) return;
      const action = target.dataset.reservationAction;
      if (target.dataset.date) this.state = selectCalendarDate(this.state, target.dataset.date);
      if (action === "open-calendar") {
        this.toggleCalendar();
        this.track("calendar_opened");
      }
      if (action === "calendar-previous") this.state = navigateCalendarMonth(this.state, -1);
      if (action === "calendar-next") this.state = navigateCalendarMonth(this.state, 1);
      if (action === "clubinho") this.state = selectModality(this.state, "clubinho");
      if (action === "rental") this.state = selectModality(this.state, "rental");
      if (action === "waitlist") this.state = joinWaitlist(this.state);
      if (action === "confirm") {
        if (this.state.config.mode === "pilot") {
          const identity = await this.refreshIdentity();
          if (!identity.authorized) return;
        }
        this.state = confirmReservation(this.state);
        if (this.state.config.mode === "pilot" && this.state.persistenceUnavailable) {
          this.pilotIdempotencyKey ||= globalThis.crypto?.randomUUID?.() || `pilot-${Date.now()}`;
          try {
            this.state = await persistPilotReservation(this.state, {
              idempotencyKey: this.pilotIdempotencyKey,
            });
          } catch (error) {
            this.state = {
              ...this.state,
              persistenceUnavailable: true,
              pilotError: String(error.message || "pilot_persistence_failed"),
            };
          }
        }
      }
      if (action === "cancel" && this.state.confirmed) {
        this.state = { ...this.state, cancelling: true, pilotError: null };
        this.render();
        try {
          this.state = await cancelPilotReservation(this.state);
          this.state = await rehydratePilotReservation(this.state);
        } catch (error) {
          this.state = { ...this.state, cancelling: false, pilotError: String(error.message || "pilot_cancel_failed") };
        }
      }
      this.flushEvents();
      this.render();
      if (["calendar-previous", "calendar-next"].includes(action)) {
        this.root.querySelector?.("[data-reservation-calendar] button:not([disabled])")?.focus();
      }
    });
    this.root.addEventListener?.("change", (event) => {
      const target = event.target;
      if (target?.matches?.("[data-reservation-region]")) {
        this.state = selectRegion(this.state, target.value);
      }
      if (target?.matches?.("[data-reservation-duration]")) {
        this.state = selectRentalDuration(this.state, target.value);
      }
      if (target?.matches?.("[data-reservation-postal-code]")) {
        this.state = { ...this.state, postalCodeValid: validatePostalCode(target.value) };
        if (!this.state.postalCodeValid) this.state.events.push(sanitizeJourneyEvent("region_unavailable", { reason: "invalid_postal_code" }));
      }
      this.flushEvents();
      this.render();
    });
    this.root.addEventListener?.("keydown", (event) => {
      const calendar = this.root.querySelector?.("[data-reservation-calendar]");
      const panel = this.root.querySelector?.("[data-reservation-calendar-panel]");
      if (event.key === "Escape" && panel && !panel.hidden) {
        this.closeCalendar();
        return;
      }
      const day = event.target?.closest?.("[data-date]");
      if (!day || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      event.preventDefault();
      const days = [...calendar.querySelectorAll("button:not([disabled])")];
      const current = days.indexOf(day);
      const delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
      days[Math.max(0, Math.min(days.length - 1, current + delta))]?.focus();
    });
  }

  async refreshIdentity() {
    if (this.state.config.mode !== "pilot") return this.state.identity;
    this.state = {
      ...this.state,
      identity: { status: "checking", authenticated: false, authorized: false },
      pilotError: null,
    };
    this.render();
    const identity = await probePilotIdentity({
      endpoint: this.state.config.runtime?.identityEndpoint,
    });
    this.state = { ...this.state, identity };
    this.state.events.push(sanitizeJourneyEvent("identity_checked", { status: identity.status }));
    this.flushEvents();
    this.render();
    return identity;
  }

  async initializePilot() {
    if (this.state.config.mode !== "pilot") {
      this.state = { ...this.state, rehydrating: false, pilotError: null };
      this.render();
      return;
    }
    const identity = await this.refreshIdentity();
    if (!identity.authorized) {
      this.state = { ...this.state, rehydrating: false };
      this.render();
      return;
    }
    try {
      this.state = await rehydratePilotReservation(this.state);
    } catch (error) {
      this.state = { ...this.state, rehydrating: false, pilotError: String(error.message || "pilot_read_failed") };
    }
    this.flushEvents();
    this.render();
  }

  toggleCalendar() {
    const panel = this.root.querySelector?.("[data-reservation-calendar-panel]");
    const trigger = this.root.querySelector?.("[data-reservation-action='open-calendar']");
    if (!panel || !trigger) return;
    panel.hidden = !panel.hidden;
    trigger.setAttribute("aria-expanded", String(!panel.hidden));
    if (!panel.hidden) this.root.querySelector?.("[data-reservation-calendar] button:not([disabled])")?.focus();
  }

  closeCalendar() {
    const panel = this.root.querySelector?.("[data-reservation-calendar-panel]");
    const trigger = this.root.querySelector?.("[data-reservation-action='open-calendar']");
    if (panel) panel.hidden = true;
    trigger?.setAttribute?.("aria-expanded", "false");
    trigger?.focus?.();
  }

  track(type, payload = {}) {
    this.state.events.push(sanitizeJourneyEvent(type, payload));
    this.flushEvents();
  }

  flushEvents() {
    const pending = createReservationObservability(this.state.events);
    this.state.events = [];
    pending.forEach((event) => {
      this.core.eventBus.emit(event.type, event);
      emit(this.root, event);
    });
  }

  render() {
    renderState(this.root, this.state);
  }
}

export function createReservationJourneyApp(root) {
  return new ReservationJourneyApp(root);
}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function boot(root) {
  if (!root || root.dataset.reservationJourneyBooted === "true") return;
  root.dataset.reservationJourneyBooted = "true";
  root.dataset.reservationJourneyInstance = createReservationJourneyApp(root).state.version;
}

if (typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(RESERVATION_JOURNEY_SELECTOR).forEach((root) => boot(root));
  });
  document.addEventListener("shopify:section:load", (event) => {
    event.target.querySelectorAll?.(RESERVATION_JOURNEY_SELECTOR).forEach((root) => boot(root));
  });
}
